import { createContext, useContext, useEffect, useRef, useCallback } from 'react';

type RefreshHandler = () => Promise<void>;

interface WebviewRefreshContextValue {
  /** 새로고침 핸들러 등록 (unmount 시 자동 해제) */
  setHandler: (handler: RefreshHandler) => void;
  /** 새로고침 핸들러 해제 */
  clearHandler: () => void;
  /** 새로고침 비활성화 */
  setDisabled: (disabled: boolean) => void;
  /** 현재 핸들러 참조 */
  handlerRef: React.MutableRefObject<RefreshHandler | null>;
  /** 현재 비활성화 여부 참조 */
  disabledRef: React.MutableRefObject<boolean>;
}

export const WebviewRefreshContext = createContext<WebviewRefreshContextValue | null>(null);

/**
 * 페이지에서 PullToRefresh 핸들러를 등록합니다.
 * unmount 시 자동으로 해제됩니다.
 *
 * @example
 * useWebviewRefresh(async () => {
 *   await refetch();
 * });
 */
export function useWebviewRefresh(handler: RefreshHandler) {
  const ctx = useContext(WebviewRefreshContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.setHandler(handler);
    return () => {
      ctx.clearHandler();
    };
  }, [ctx, handler]);
}

/**
 * 특정 페이지에서 PullToRefresh를 비활성화합니다.
 * unmount 시 자동으로 복원됩니다.
 *
 * @example
 * useDisableWebviewRefresh();
 */
export function useDisableWebviewRefresh() {
  const ctx = useContext(WebviewRefreshContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.setDisabled(true);
    return () => {
      ctx.setDisabled(false);
    };
  }, [ctx]);
}
